import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import FilterIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Typography, Grid, Box, Button, Divider } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useTranslation } from 'next-i18next';
import { scroller } from 'react-scroll';
import useSWR from 'swr';

import CourseFilterItem from './courseFilterItem';
import { CourseFilterStyle, CourseFilterDrawerStyle } from './style';

import { APP_END_POINT } from 'src/api/app';
import { useFilterItems } from 'src/helpers/use';
import { ICategory } from 'src/interfaces';
import { CourseFilterObjectProps, CourseFiltersKey } from 'src/interfaces/course';
import courseActions from 'src/redux/course/actions';
import { RootState } from 'src/redux/reducer';

const { add_course_api_query, clear_course_filter } = courseActions;

type CourseFilterProps = {
  onScroll?: (event: any) => void;
  filters: CourseFilterObjectProps[];
};

type OnlyCourseFilters = {
  categoryId?: string[];
  priceRange?: string[];
  haveCertificate?: string[];
};

const CourseFilter = ({ onScroll, filters }: CourseFilterProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { courseApiQueries } = useSelector((state: RootState) => ({
    courseApiQueries: state.Course.get('courseApiQueries'),
  }));
  const [filterAmount, setFilterAmount] = useState<number>(0);

  const handleFilters = (e: React.ChangeEvent<HTMLInputElement>, key: CourseFiltersKey) => {
    const { value } = e.target;
    const updatedValue = (() => {
      const courseApiQuery = courseApiQueries[key] as string[] | undefined;
      if (courseApiQuery) {
        if (courseApiQuery.includes(value)) {
          return { [key]: courseApiQuery.filter((item: string) => item !== value) };
        }
        return { [key]: [value, ...courseApiQuery] };
      }
      return { [key]: [value] };
    })();
    dispatch(add_course_api_query(updatedValue));
    scroller.scrollTo('courseResultHeader', {
      duration: 1000,
      smooth: true,
      offset: -100,
    });
  };

  const resetFilterClick = () => {
    dispatch(clear_course_filter());
    setFilterAmount(0);
  };

  const FilterCountCard = ({ amount }: { amount: number }) => (
    <Typography variant="h4" component="span" sx={{ m: 1 }}>
      ({amount})
    </Typography>
  );

  useEffect(() => {
    const { sortBy, orderBy, q, ...onlyCourseFilters } = courseApiQueries;
    const amount = Object.keys(onlyCourseFilters).reduce<number>((total, key) => {
      const onlyCourseFilter = onlyCourseFilters[key as keyof OnlyCourseFilters] as string[] | undefined;
      return total + (onlyCourseFilter?.length || 0);
    }, 0);
    setFilterAmount(amount);
  }, [courseApiQueries]);

  return (
    <CourseFilterStyle sx={{ width: '100%', bgcolor: 'white' }} className="filter-container">
      <Box sx={{ my: 1, mx: 2 }} className="filter-header">
        <Grid container alignItems="center" sx={{ height: '47px' }}>
          <Grid item xs>
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
              <Typography variant="h4" component="div" color="primary" className="title">
                <IconButton aria-label="filter">
                  <FilterIcon color="primary" sx={{ fontSize: 20 }} />
                </IconButton>
                {t('all_course.filter.title')}
                {filterAmount ? <FilterCountCard amount={filterAmount} /> : null}
              </Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
              <Typography variant="h4" component="div" className="title">
                {t('all_course.filter.title')}
                {filterAmount ? <FilterCountCard amount={filterAmount} /> : null}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            {filterAmount ? (
              <Grid container justifyContent="center">
                <Button className="reset-filter-button" startIcon={<RefreshIcon />} onClick={resetFilterClick}>
                  {t('all_course.filter.reset_filter')}
                </Button>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Box>
      <Box className="filter-content">
        <List className="filter-list-container" onScroll={onScroll}>
          {filters.map((filter) => (
            <ListItem sx={{ p: 0 }} key={`filter-${filter.key}`}>
              <CourseFilterItem courseApiQueries={courseApiQueries} handleFilters={handleFilters} object={filter} />
            </ListItem>
          ))}
        </List>
      </Box>
    </CourseFilterStyle>
  );
};

export default CourseFilter;

type CourseFilterDrawerProps = {
  isOpen: boolean;
  anchor: string;
  onClose: (event: any) => void;
};

export const CourseFilterDrawer = ({ isOpen, anchor, onClose }: CourseFilterDrawerProps) => {
  const [scrollTopPosition, setScrollTopPosition] = useState(0);
  const { data: categories } = useSWR<ICategory[]>(APP_END_POINT.CATEGORIES);
  const { data: category } = useSWR<ICategory>('CATEGORY');
  const { data: subcategory } = useSWR<ICategory>('SUBCATEGORY');
  const { filters } = useFilterItems(categories || (!subcategory ? category?.children : undefined), !!category);
  const { t } = useTranslation('common');

  const handleOnScroll = (event: any) => {
    setScrollTopPosition(event.target.scrollTop);
  };

  return (
    <CourseFilterDrawerStyle isScroll={scrollTopPosition > 10} anchor={anchor} open={isOpen} onClose={onClose}>
      <Box className="close-button-container" onClick={onClose}>
        <IconButton color="primary" className="close-button">
          <CloseIcon />
        </IconButton>
      </Box>
      <CourseFilter filters={filters} onScroll={handleOnScroll} />
      <Box className="submit-button-container">
        <Divider className="submit-line-separator" />
        <Grid container justifyContent="center">
          <Button sx={{ m: 2 }} variant="contained" color="primary" fullWidth onClick={onClose}>
            {t('all_course.filter.submit')}
          </Button>
        </Grid>
      </Box>
    </CourseFilterDrawerStyle>
  );
};
